import { useQueryClient } from '@tanstack/react-query';

import { useActivities } from '../api/get-activity';

interface UseActivitiesDataProps {
  search_by: string;
  search_query: string;
  order_by: string;
  direction: string;
  status: string;
  start_date: number | null;
  end_date: number | null;
  distribution_channel: string;
  cg_1_business_segmentation: string[];
}

export const useActivitiesData = (queryString: UseActivitiesDataProps) => {
  const queryClient = useQueryClient();

  const {
    activitiesData,
    total,
    pageCount,
    refetch,
    isLoading,
    isFetched,
    isFetching,
  } = useActivities(queryString);

  const tableData = activitiesData?.data ? activitiesData.data : [];

  // Enhanced refetch with cache invalidation
  const enhancedRefetch = async () => {
    await queryClient.invalidateQueries({ queryKey: ['Activities'] });
    return refetch();
  };

  // Prefetch next page for better UX
  const prefetchNextPage = async (nextPage: number) => {
    if (nextPage <= pageCount) {
      await queryClient.prefetchQuery({
        queryKey: ['Activities', { ...queryString, page: nextPage }],
        queryFn: () => refetch(),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    }
  };

  return {
    // Data
    activitiesData,
    tableData,
    total,
    pageCount,

    // Loading states
    isLoading,
    isFetched,
    isFetching,

    // Actions
    refetch: enhancedRefetch,
    prefetchNextPage,

    // Computed values
    hasData: tableData.length > 0,
    isEmpty: isFetched && tableData.length === 0,
    isError: false, // Add error handling if needed
  };
};
